import { centralYMD } from "./weatherZoneJoin";

/**
 * Central-timezone day bounds, in epoch milliseconds.
 *
 * WHY THIS EXISTS. The schedule's day semantics are Central, not the browser's:
 * all-day calendar items are anchored at Central noon (see AllDayEventStrip),
 * and the weather join already keys off `centralYMD`. A "Today" panel built
 * from `new Date().setHours(0,0,0,0)` therefore shows the right day only for a
 * viewer who happens to be in Central — a travelling owner, or a developer
 * verifying against prod from another zone, silently gets the wrong day, and a
 * test that "passed" proves nothing.
 *
 * WHY THE PROBE, rather than subtracting the local clock time. Subtracting
 * "hours since midnight" is wrong across a DST transition: at 10:00 CDT on a
 * spring-forward day only nine hours have actually elapsed since midnight, so
 * subtracting ten lands on the previous evening. Instead we construct the two
 * candidate UTC instants for local midnight (Central is UTC-6 in CST, UTC-5 in
 * CDT) and keep whichever one formats back to the same Central calendar date at
 * hour 00. Central's spring-forward gap is 02:00→03:00, so local midnight
 * always exists and exactly one candidate matches.
 */

const CENTRAL = "America/Chicago";

const centralParts = (ms) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: CENTRAL,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
  }).formatToParts(new Date(ms));
  const pick = (type) => parts.find((p) => p.type === type)?.value;
  return {
    ymd: `${pick("year")}-${pick("month")}-${pick("day")}`,
    hour: pick("hour"),
  };
};

/** Epoch-ms of 00:00:00.000 Central on the Central day containing `nowMs`. */
export const centralDayStartMs = (nowMs = Date.now()) => {
  const ymd = centralYMD(nowMs);
  if (!ymd) return null;
  const [y, m, d] = ymd.split("-").map(Number);

  for (const offsetHours of [5, 6]) {
    const guess = Date.UTC(y, m - 1, d, offsetHours, 0, 0, 0);
    const got = centralParts(guess);
    /* Some locales render midnight as "24" rather than "00". */
    if (got.ymd === ymd && (got.hour === "00" || got.hour === "24")) {
      return guess;
    }
  }
  /* Unreachable for Central, but a wrong hour beats a null day. */
  return Date.UTC(y, m - 1, d, 6, 0, 0, 0);
};

/**
 * `[startMs, endMs]` for the Central day containing `nowMs`, as the STRINGS the
 * schedule API expects (every time in this app is an epoch-ms string).
 * `endMs` is the last millisecond of the day, so both bounds are inclusive —
 * which is what `splitEventForDay` compares against.
 */
export const centralDayRange = (nowMs = Date.now()) => {
  const start = centralDayStartMs(nowMs);
  if (start == null) return null;
  /* Next local midnight, then step back 1ms. Computed from the NEXT day's
     own probe rather than start + 24h, so a DST day is 23 or 25 hours. */
  const nextDayStart = centralDayStartMs(start + 36 * 60 * 60 * 1000);
  return {
    rangeStart: String(start),
    rangeEnd: String(nextDayStart - 1),
    dayStartMs: start,
    dayEndMs: nextDayStart - 1,
  };
};
